import { ERROR_CODES, type ErrorCode, type Issue, PRODUCT_NAME } from '@tallyframe/core';

/**
 * Context for describing issue locations in human-readable terms.
 * Provides mappings from ids to human-readable titles and labels.
 */
export interface IssueContext {
  widgetTitles?: Record<string, string>;
  columnLabels?: Record<string, string>;
}

/**
 * A server error, decoded from the one envelope every endpoint uses:
 * `{ error: { code, message, issues, details } }` (contracts §4).
 *
 * The point of carrying `issues` all the way into the UI is that an error has
 * to tell somebody what to fix. "Upload rejected" is useless; "row 14,
 * Actual hours: expected a number, got \"62.5 hours\"" is actionable.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly issues: Issue[];
  readonly details: Record<string, unknown>;

  constructor(
    code: ErrorCode,
    message: string,
    status: number,
    issues: Issue[] = [],
    details: Record<string, unknown> = {}
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.issues = issues.length > 0 ? issues : [{ code, message }];
    this.details = details;
  }

  static isApiError(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }

  /** True when the caller can fix this by changing what they sent. `INTERNAL`
   *  and the export faults are explicitly *not* the caller's fault, so we never
   *  ask somebody to correct input that was already correct. */
  get isCallerFixable(): boolean {
    switch (this.code) {
      case 'INTERNAL':
      case 'EXPORT_FAILED':
      case 'EXPORT_BUSY':
      case 'EXPORT_TIMEOUT':
        return false;
      default:
        return true;
    }
  }
}

function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value);
}

function asIssues(value: unknown): Issue[] {
  if (!Array.isArray(value)) return [];
  const out: Issue[] = [];
  for (const raw of value) {
    if (typeof raw !== 'object' || raw === null) continue;
    const candidate = raw as { code?: unknown; message?: unknown; location?: unknown };
    if (!isErrorCode(candidate.code) || typeof candidate.message !== 'string') continue;
    const issue: Issue = { code: candidate.code, message: candidate.message };
    if (typeof candidate.location === 'object' && candidate.location !== null) {
      issue.location = candidate.location as Issue['location'];
    }
    out.push(issue);
  }
  return out;
}

/**
 * Turns any failed response into an `ApiError`.
 *
 * A response that is not the documented envelope — an HTML error page from a
 * proxy, say — still has to become a typed error rather than a parse crash,
 * because the UI's job at that point is to say something truthful.
 */
export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }
  const envelope =
    typeof body === 'object' && body !== null ? (body as { error?: unknown }).error : undefined;

  if (typeof envelope === 'object' && envelope !== null) {
    const shape = envelope as { code?: unknown; message?: unknown; issues?: unknown; details?: unknown };
    const code: ErrorCode = isErrorCode(shape.code) ? shape.code : 'INTERNAL';
    const message =
      typeof shape.message === 'string' && shape.message.length > 0
        ? shape.message
        : `Request failed with status ${response.status}.`;
    const details =
      typeof shape.details === 'object' && shape.details !== null
        ? (shape.details as Record<string, unknown>)
        : {};
    return new ApiError(code, message, response.status, asIssues(shape.issues), details);
  }

  return new ApiError(
    response.status === 401 ? 'UNAUTHENTICATED' : 'INTERNAL',
    `The server returned ${response.status} ${response.statusText || ''}`.trim() + '.',
    response.status
  );
}

/** A network failure (server down, DNS, offline) presented in the same shape. */
export function apiErrorFromNetwork(cause: unknown): ApiError {
  const detail = cause instanceof Error ? cause.message : String(cause);
  return new ApiError(
    'INTERNAL',
    `Could not reach the ${PRODUCT_NAME} server. ${detail}`,
    0
  );
}

/** One-line description of where an issue happened, for display next to it. */
export function describeIssueLocation(issue: Issue, context: IssueContext = {}): string | null {
  const location = issue.location;
  if (!location) return null;
  const parts: string[] = [];
  if (typeof location.row === 'number') parts.push(`row ${location.row + 1}`);
  if (location.columnId) {
    const label = context.columnLabels?.[location.columnId];
    parts.push(label ? label : 'a column');
  }
  if (location.widgetId) {
    const title = context.widgetTitles?.[location.widgetId];
    parts.push(title ? title : 'a widget');
  }
  if (location.path) parts.push(location.path);
  return parts.length > 0 ? parts.join(' · ') : null;
}
